<template>
  <div>
    <Teleport v-if="afetrTitleActionSpaceMounted" to="#page-title-action-space">
      <div
        class="d-flex align-items-center"
        :class="{
          'mt-2': isMobile,
        }"
      >
        <BasicButton
          label="新規作成"
          class="add-btn me-2"
          variant="secondary"
          icon="pi pi-plus"
          outlined
          @click="
            goto({
              name: 'UsersNewsNew',
              query: { organizationId: selectedOrganizationId },
            })
          "
        />

        <BasicButton
          label="ゴミ箱"
          variant="danger"
          outlined
          icon="pi pi-trash"
          @click="
            goto({
              name: 'UsersNewsTrashIndex',
              query: { organizationId: selectedOrganizationId },
            })
          "
        />
      </div>
    </Teleport>

    <UserPageActions>
      <Card class="w-100">
        <template #content>
          <NewsFilter
            v-model:selected-organization-id="selectedOrganizationId"
            v-model:selected-for-members="selectedForMembers"
            v-model:selected-news-label="selectedNewsLabel"
            :organizations="organizations"
            :news-filter-options="newsFilterOptions"
            :news-labels="newsLabels"
          />
        </template>
      </Card>
    </UserPageActions>

    <Card>
      <template #content>
        <span v-if="news && !news.length" class="text-danger"
          >お知らせがありません</span
        >
        <NewsTable
          v-else-if="filteredNews"
          :news="filteredNews"
          @select-news="selectNews"
          @trash-news="handleTrashNews"
        />
        <i v-else class="pi pi-spin pi-spinner"></i>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, inject } from "vue";
import { getStorage, setStorage } from "/@/modules/localStorage";
import {
  useMqUtils,
  useRouterUtil,
  useUser,
  useNews,
} from "/@/vue/composables";
import { BasicButton } from "/@/vue/components/Atom";
import { UserPageActions } from "/@/vue/components/Layouts";
import {
  Table as NewsTable,
  Filter as NewsFilter,
} from "/@/vue/components/Organisms/Users/News";
import Dropdown from "primevue/dropdown";
import Card from "primevue/card";
import { NewsClient, newsLabels } from "/@/types";
import { labelTranslater } from "/@/modules/news";

const afetrTitleActionSpaceMounted = inject("afetrTitleActionSpaceMounted");

const { isMobile } = useMqUtils();
const { goto } = useRouterUtil();

function selectNews(id: number) {
  goto({ name: "UsersNewsShow", params: { id: id } });
}

// api

const { getUser } = useUser();
const { data: user } = getUser();

const organizations = computed(() => {
  if (!user.value) return [];

  return user.value.managedOrganizations;
});

const selectedOrganizationId = ref(
  getStorage("selectedOrganizationId") || undefined
);

watch(organizations, (os) => {
  if (!os) return;

  if (os.some((o) => o.id === selectedOrganizationId.value)) {
    return;
  }

  selectedOrganizationId.value = os[0]?.id;
});

watch(selectedOrganizationId, (id) => {
  setStorage("selectedOrganizationId", id);
});

watch(user, (u) => {
  if (!u || !!selectedOrganizationId.value) return;

  selectedOrganizationId.value = u.managedOrganizations[0]?.id;
});

const {
  getUsersNews,
  trashNews,
  selectedNewsLabel,
  selectedForMembers,
  newsFilterOptions,
  newsFilter,
} = useNews();
const { data: news, mutate: getNewsMutate } = getUsersNews(
  selectedOrganizationId
);

const filteredNews = computed(() => {
  if (!news.value) return [];

  return newsFilter(
    news.value,
    selectedNewsLabel.value,
    selectedForMembers.value
  );
});

const goTrashMessage =
  "ゴミ箱に移動しますか？ ゴミ箱の記事は一定期間後に完全に削除されます。";

async function handleTrashNews(id: number | number[]) {
  if (!window.confirm(goTrashMessage)) {
    return;
  }

  if (await trashNews(id)) {
    alert("ゴミ箱に移動しました");
    getNewsMutate();
    goto({ name: "UsersNewsIndex" });
  } else {
    alert("移動に失敗しました");
  }
}
</script>

<style lang="scss" scoped></style>
